/**
 * El color de un producto, como se ve.
 *
 * Un cuadradito y nada más: la palabra "SIERRA BLUE" repetida en veinte filas
 * ocupa un ancho y no se lee; el color se reconoce de un vistazo, que es como
 * se elige un equipo en el mostrador («el azulito», no «el Sierra Blue»).
 *
 * Vive en `catalogo/` y no en `usados/` porque el color es un atributo de
 * cualquier producto: lo usan la lista de usados, la de iPhones sellados y el
 * buscador global.
 *
 * El nombre no se pierde — va en el `title`, en el nombre accesible y en el
 * buscador, que mira también el campo `color`.
 *
 * Un color que la tabla no conoce se muestra como texto. Antes que dibujar un
 * cuadrado de un color que no es, se escribe lo que alguien escribió.
 */
import { colorDeIphone } from "./colores";

export function MuestraColor({ color }: { color: string | null }): JSX.Element {
  if (!color?.trim()) return <span className="text-muted-foreground">—</span>;

  const apple = colorDeIphone(color);
  if (!apple) return <span className="text-sm">{color}</span>;

  return (
    <span className="flex items-center" title={apple.etiqueta}>
      <span
        aria-hidden="true"
        className="size-4 shrink-0 rounded-sm border border-border"
        style={{ backgroundColor: apple.hex }}
      />
      <span className="sr-only">{apple.etiqueta}</span>
    </span>
  );
}

/**
 * Los colores en que viene un modelo, uno al lado del otro.
 *
 * Es la respuesta a «¿en qué colores viene el 17 Pro?», que hasta ahora no
 * estaba en ninguna parte del sistema salvo en la cabeza del que atiende. Un
 * sellado no tiene un color: se pide en el que el cliente quiera.
 *
 * Los cuadraditos van más chicos que el de una unidad usada —son cuatro o
 * cinco en el ancho de una celda— y el nombre de cada uno queda en el `title`
 * y en el texto accesible, que es donde se lee «Cosmic Orange» sin ocupar
 * media tabla.
 */
export function MuestraColores({
  colores,
}: {
  colores: ReadonlyArray<{ etiqueta: string; hex: string }>;
}): JSX.Element {
  if (colores.length === 0) return <span className="text-muted-foreground">—</span>;

  const nombres = colores.map((c) => c.etiqueta).join(", ");
  return (
    <span className="flex items-center gap-1" title={nombres}>
      {colores.map((c) => (
        <span
          key={c.etiqueta}
          aria-hidden="true"
          className="size-3.5 shrink-0 rounded-sm border border-border"
          style={{ backgroundColor: c.hex }}
        />
      ))}
      <span className="sr-only">Viene en {nombres}</span>
    </span>
  );
}
